/**
 * Форма редактирования номера (правки Артёма, «Настройки» → «Номера»).
 *
 * Отдельный экран (НЕ инлайн): открывается проваливанием по номеру из списка.
 * Поля: тип номера, этаж, № комнаты (с проверкой уникальности на бэке —
 * нельзя задать номер, уже занятый другим номером отеля → 409, понятная
 * ошибка).
 *
 * Правка Артёма 2026-06-17: в настройках номера — ТОЛЬКО номерной фонд
 * (тип / этаж / №). Управление фото номера убрано: фотографии живут у ТИПА
 * номера (RoomTypeForm), у физического номера статусов/фото нет. Бэк
 * room_photos остаётся, но форма номера их больше не отправляет.
 *
 * Бэк: GET /v1/rooms/{id} → PATCH /v1/rooms/{id} (number/floor/type_id) —
 * гейт mgmt+housekeeper (#331).
 *
 * Кнопки СНИЗУ: «Сохранить» и «Назад» (canon формы редактирования).
 * i18n русский; мобайл 375 — один столбец (.hsc-stack).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { RoomTypeView } from '@hsc/types';
import { useMe } from '../../shared/api/useMe.js';
import { useRoom, useRoomTypesAdmin, useUpdateRoom } from './useRooms.js';

function roomTypeName(rt: RoomTypeView): string {
  return rt.display_name?.ru ?? rt.display_name?.en ?? rt.code;
}

export interface RoomEditFormProps {
  /** id номера для правки. */
  roomId: string;
  /** Вернуться к списку (кнопка «Назад» / после сохранения). */
  onBack: () => void;
}

export function RoomEditForm({ roomId, onBack }: RoomEditFormProps): JSX.Element {
  const me = useMe();
  const tenantId = me.data?.tenant_id;
  const roomQ = useRoom(roomId);
  const typesQ = useRoomTypesAdmin(tenantId);
  const update = useUpdateRoom();

  const [typeId, setTypeId] = useState('');
  const [number, setNumber] = useState('');
  const [floor, setFloor] = useState('0');
  const [dup, setDup] = useState(false);

  // Гидратация формы из карточки номера (один раз по приходу данных).
  useEffect(() => {
    const r = roomQ.data;
    if (!r) return;
    setTypeId(r.type_id);
    setNumber(r.number);
    setFloor(String(r.floor));
  }, [roomQ.data]);

  const types = useMemo(() => typesQ.data ?? [], [typesQ.data]);

  const busy = update.isPending;
  const canSubmit = number.trim().length > 0 && typeId !== '' && !busy;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setDup(false);
    if (!canSubmit) return;
    update.mutate(
      {
        id: roomId,
        input: {
          number: number.trim(),
          floor: Number.parseInt(floor, 10) || 0,
          type_id: typeId,
        },
      },
      {
        onSuccess: onBack,
        onError: (err) => {
          // 409 room.number_taken → понятная ошибка про дубль номера.
          const status = (err as { status?: number }).status;
          const msg = err.message ?? '';
          if (status === 409 || /number_taken|409|conflict/i.test(msg)) setDup(true);
        },
      },
    );
  };

  if (roomQ.isLoading) {
    return (
      <section data-testid="room-edit-form">
        <Skeleton height={320} radius={16} />
      </section>
    );
  }
  if (roomQ.isError || !roomQ.data) {
    return (
      <section data-testid="room-edit-form">
        <Card>
          <p role="alert">Не удалось загрузить номер. Попробуйте обновить страницу.</p>
          <div className="fleet-card__actions">
            <Button variant="secondary" onClick={onBack} data-testid="room-edit-back">
              Назад
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="room-edit-form">
      <div className="fleet-drawer__head">
        <h1 className="hsc-page__title">Номер № {roomQ.data.number}</h1>
      </div>

      <Card>
        <form className="hsc-stack" onSubmit={handleSubmit}>
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="room-edit-type">
              Тип номера
            </label>
            <select
              id="room-edit-type"
              className="ta-select"
              data-testid="room-edit-type"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              required
            >
              <option value="">— выберите тип —</option>
              {types.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {roomTypeName(rt)}
                </option>
              ))}
            </select>
          </div>

          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="room-edit-number">
              Номер комнаты
            </label>
            <input
              id="room-edit-number"
              className="ta-input"
              data-testid="room-edit-number"
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
                setDup(false);
              }}
              placeholder="Например, 101"
              maxLength={8}
              aria-invalid={dup}
              required
            />
            {dup && (
              <span className="fleet-card__err" role="alert" data-testid="room-edit-number-dup">
                Этот номер уже занят другим номером отеля. Укажите свободный.
              </span>
            )}
          </div>

          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="room-edit-floor">
              Этаж
            </label>
            <input
              id="room-edit-floor"
              type="number"
              className="ta-input"
              data-testid="room-edit-floor"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              min={0}
              max={50}
            />
          </div>

          {update.isError && !dup && (
            <p role="alert" className="fleet-card__err" data-testid="room-edit-error">
              Не удалось сохранить номер. Проверьте данные и попробуйте снова.
            </p>
          )}

          {/* Кнопки СНИЗУ: Сохранить + Назад. */}
          <div className="room-edit__footer">
            <Button type="submit" disabled={!canSubmit} data-testid="room-edit-save">
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onBack}
              data-testid="room-edit-back"
            >
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}
